import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { CheckCircle, XCircle, Loader2, ShoppingBag } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const processedRef = useRef(false);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/');
      return;
    }

    if (processedRef.current) return;
    processedRef.current = true;

    pollPaymentStatus(sessionId, 0);
  }, [sessionId]);

  const pollPaymentStatus = async (sid, attempts) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/stripe/checkout-status/${sid}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(data);
        clearCart();
        return;
      } else if (data.status === 'expired') {
        setStatus('canceled');
        return;
      }

      setStatus('processing');
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="py-20 md:py-32 min-h-[60vh]" data-testid="payment-success-page">
      <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
        {(status === 'checking' || status === 'processing') && (
          <div className="space-y-6" data-testid="payment-processing">
            <Loader2 className="w-16 h-16 text-[#B87333] mx-auto animate-spin" />
            <h1 className="text-4xl md:text-5xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
              Processing Payment
            </h1>
            <p className="text-[#5D4037] text-lg">
              Please wait while we confirm your payment...
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-6" data-testid="payment-success">
            <CheckCircle className="w-20 h-20 text-green-600 mx-auto" />
            <h1 className="text-4xl md:text-5xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
              Payment Successful!
            </h1>
            <p className="text-[#5D4037] text-lg">
              Thank you for your purchase. Your order has been confirmed.
            </p>
            {paymentData && (
              <div className="bg-white/40 border border-[#D7C0A0] p-6 mt-8 inline-block text-left" data-testid="payment-details">
                <h3 className="font-medium mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Payment Details
                </h3>
                <div className="space-y-2 text-sm text-[#5D4037]">
                  <p><span className="font-medium">Amount:</span> ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}</p>
                  <p><span className="font-medium">Status:</span> {paymentData.payment_status}</p>
                  <p><span className="font-medium">Session:</span> {paymentData.session_id}</p>
                </div>
              </div>
            )}
            <div className="mt-8">
              <button
                onClick={() => navigate('/')}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="continue-shopping-button"
              >
                <ShoppingBag className="w-4 h-4" />
                Continue Shopping
              </button>
            </div>
          </div>
        )}

        {(status === 'error' || status === 'timeout') && (
          <div className="space-y-6" data-testid="payment-error">
            <XCircle className="w-20 h-20 text-amber-500 mx-auto" />
            <h1 className="text-4xl md:text-5xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
              Payment Status Unknown
            </h1>
            <p className="text-[#5D4037] text-lg">
              We couldn't confirm your payment status. If you were charged, your order will still be processed.
            </p>
            <div className="mt-8 flex gap-4 justify-center">
              <button
                onClick={() => navigate('/')}
                className="btn-primary"
                data-testid="go-home-button"
              >
                Go Home
              </button>
            </div>
          </div>
        )}

        {status === 'canceled' && (
          <div className="space-y-6" data-testid="payment-canceled">
            <XCircle className="w-20 h-20 text-red-500 mx-auto" />
            <h1 className="text-4xl md:text-5xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
              Payment Canceled
            </h1>
            <p className="text-[#5D4037] text-lg">
              Your payment was canceled. Please try again.
            </p>
            <div className="mt-8">
              <button
                onClick={() => navigate('/checkout')}
                className="btn-primary"
                data-testid="retry-checkout-button"
              >
                Return to Checkout
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
